/**
 *
 * User: bmiller
 * Original: 2011-04-20
 * Date: 2019-06-14
 * Time: 2:01 PM
 * This change marks the beginning of version 4.0 of the runestone components
 * Login/logout is no longer handled through javascript but rather server side.
 * Many of the components depend on the runestone:login event so we will keep that
 * for now to keep the churn fairly minimal.
 */

/*

 Copyright (C) 2011  Brad Miller  bonelake@gmail.com

 This program is free software: you can redistribute it and/or modify
 it under the terms of the GNU General Public License as published by
 the Free Software Foundation, either version 3 of the License, or
 (at your option) any later version.

 This program is distributed in the hope that it will be useful,
 but WITHOUT ANY WARRANTY; without even the implied warranty of
 MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 GNU General Public License for more details.

 You should have received a copy of the GNU General Public License
 along with this program.  If not, see <http://www.gnu.org/licenses/>.

 */

import RunestoneBase from "./runestonebase.js";
import { marked } from "marked";

var rb = new RunestoneBase();

// ---------------------------------------------------------------------------
// Console capture for problem reports
//
// The browser gives page scripts no way to read what is already in the devtools
// console, so we buffer console output ourselves from the moment this module is
// evaluated.  When the user opens the "Report a Problem" form we stash the
// buffer in sessionStorage (see the Problem Report menu link below); the form
// reads it back into a hidden field so recent log output and uncaught errors
// ride along with the report.
// ---------------------------------------------------------------------------
export const RS_CONSOLE_LOG_KEY = "rs_console_log";
const RS_LOG_BUFFER_MAX = 200;
const rsConsoleBuffer = [];

function rsFormatLogArg(arg) {
    if (typeof arg === "string") return arg;
    if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
    try {
        return JSON.stringify(arg);
    } catch (e) {
        return String(arg);
    }
}

function rsRecordLog(level, args) {
    try {
        const line = `[${level}] ${Array.from(args).map(rsFormatLogArg).join(" ")}`;
        rsConsoleBuffer.push(line);
        if (rsConsoleBuffer.length > RS_LOG_BUFFER_MAX) rsConsoleBuffer.shift();
    } catch (e) {
        // Never let log capture throw and break the page.
    }
}

(function installConsoleCapture() {
    if (window.__rsConsoleCaptureInstalled) return;
    window.__rsConsoleCaptureInstalled = true;
    for (const level of ["log", "info", "warn", "error"]) {
        const orig = console[level] ? console[level].bind(console) : null;
        console[level] = function (...args) {
            rsRecordLog(level, args);
            if (orig) orig(...args);
        };
    }
    // console.log() never sees uncaught errors or rejected promises, so listen
    // for those directly.
    window.addEventListener("error", (e) => {
        if (e.message) {
            rsRecordLog("uncaught", [
                `${e.message} @ ${e.filename || "?"}:${e.lineno || "?"}`,
            ]);
        }
    });
    window.addEventListener("unhandledrejection", (e) => {
        rsRecordLog("promise", [rsFormatLogArg(e.reason)]);
    });
})();

/** Recent buffered console output, newest last, as a single string. */
export function rsGetConsoleLog() {
    return rsConsoleBuffer.join("\n");
}

//
// Page decoration functions
//
/*
Maybe something like this at the top:

Active assignment: [Ch 15 reading]      [Exit assignment link]
On page (3 of 7) [Select input showing current page, can select others]
Becoming this if not on a page in assignment

Active assignment: [Ch 15 reading]      [Exit assignment link]
This page is not part of that assignment. Select a page to return to it:
[Select input]
*/
function addReadingList() {
    let assignment_info_string = localStorage.getItem(
        `currentAssignmentInfo_${eBookConfig.course}`,
    );

    if (assignment_info_string && eBookConfig.readings) {
        var top,
            bottom,
            active,
            page_name,
            exit_link,
            fst,
            snd,
            new_pos,
            path_parts,
            new_pos_link;
        var assignment_info = JSON.parse(assignment_info_string);
        let assignment_id = assignment_info.id;
        let assignment_name = assignment_info.name;
        let reading_names = assignment_info.readingNames;

        active = document.createElement("div");
        active.textContent = "Active assignment: ";

        page_name = document.createElement("a");
        page_name.textContent = assignment_name;
        page_name.href = `/assignment/student/doAssignment?assignment_id=${assignment_id}`;

        active.append(page_name);

        exit_link = document.createElement("a");
        exit_link.textContent = " Exit Assignment";
        exit_link.href = window.location.pathname;

        exit_link.addEventListener("click", function (event) {
            localStorage.removeItem(
                `currentAssignmentInfo_${eBookConfig.course}`,
            );
        });

        //active.append(exit_link)

        let cur_path_parts = window.location.pathname.split("/");
        let name =
            cur_path_parts[cur_path_parts.length - 2] +
            "/" +
            cur_path_parts[cur_path_parts.length - 1];
        // if body has pretext class, then strip the leading path parts from each of the strings in eBookConfig.readings
        let body = document.getElementsByTagName("body")[0];
        let ptxbook = false;
        let endLop = 2;
        if (body.classList.contains("pretext")) {
            ptxbook = true;
            eBookConfig.readings = eBookConfig.readings.map((r) =>
                r.split("/").pop(),
            );
            name = name.split("/").pop();
            endLop = 1;
        }

        let position = eBookConfig.readings.indexOf(name);
        let num_readings = eBookConfig.readings.length;
        // get prev name
        if (position > 0) {
            new_pos = eBookConfig.readings[position - 1];
            path_parts = cur_path_parts.slice(
                0,
                cur_path_parts.length - endLop,
            );
            path_parts.push(new_pos);
            new_pos_link = path_parts.join("/");
            fst = active.cloneNode(true);
            let txt = document.createElement("p");
            txt.textContent = `Page ${position + 1} of ${num_readings}, `;
            var fst_lnk = document.createElement("a");
            //fst_lnk.className = "btn btn-lg reading-navigation prev-reading";
            fst_lnk.href = new_pos_link;
            fst_lnk.textContent = `Back to page ${position
                } of ${num_readings}: ${reading_names[position - 1]}.`;
            txt.append(fst_lnk);
            fst.append(txt);
        } else if (position == 0) {
            fst = active.cloneNode(true);
            let txt = document.createElement("p");
            txt.textContent = `Page 1 of ${num_readings}.`;
            fst.append(txt);
        } else {
            // this isn't a reading page in the assignment, check to see if any
            // activities on this page are assigned to the current assignment
            let exerciseOnPage = false;
            let pageExercises = Object.keys(componentMap);
            if (pageExercises.length == 0) {
                pageExercises = document.querySelectorAll("[data-component]");
                pageExercises = Array.from(pageExercises).map(function (el) {
                    return el.id;
                });
            }
            for (let ex of pageExercises) {
                if (assignment_info.questions.includes(ex)) {
                    exerciseOnPage = true;
                    break;
                }
            }
            new_pos = eBookConfig.readings[0];
            path_parts = cur_path_parts.slice(
                0,
                cur_path_parts.length - endLop,
            );
            path_parts.push(new_pos);
            new_pos_link = path_parts.join("/");
            fst = active.cloneNode(true);
            let txt = document.createElement("p");
            if (exerciseOnPage) {
                txt.textContent =
                    "This page has activities assigned to the current assignment.";
            } else {
                txt.textContent =
                    "Notice: this page is not part of the assignment.";
            }
            txt.append(exit_link);
            fst.append(txt);
        }
        if (position == eBookConfig.readings.length - 1) {
            // no more readings
            snd = active;
            let txt = document.createElement("p");
            txt.textContent = `Page ${num_readings} of ${num_readings}: ${reading_names[position]}`;
            snd.append(txt);
        } else if (position >= 0) {
            // get next name
            new_pos = eBookConfig.readings[position + 1];
            path_parts = cur_path_parts.slice(
                0,
                cur_path_parts.length - endLop,
            );
            path_parts.push(new_pos);
            new_pos_link = path_parts.join("/");
            snd = active;
            var snd_lnk = document.createElement("a");
            //snd_lnk.className = "btn btn-lg reading-navigation next-reading";
            snd_lnk.href = new_pos_link;
            snd_lnk.textContent = `Continue to page ${position + 2
                } of ${num_readings}: ${reading_names[position + 1]}`;
            let txt = document.createElement("p");
            txt.append(snd_lnk);
            snd.append(txt);
        } else {
            snd = active.cloneNode(true);
            let txt = document.createElement("p");
            txt.textContent =
                "Notice: this page is not part of the assignment. To remove this warning click ";
            let exit_clone = exit_link.cloneNode(true);

            exit_clone.addEventListener("click", function (event) {
                localStorage.removeItem(
                    `currentAssignmentInfo_${eBookConfig.course}`,
                );
            });
            txt.append(exit_clone);
            snd.append(txt);
        }

        top = document.createElement("div");
        top.className = "ptx-runestone-container";
        fst.className = "runestone assignment-nav top-assignment-nav";
        //top.style.backgroundColor = "var(--componentBgColor)"
        //top.style.borderColor = "var(--componentBorderColor)"
        //top.style.borderWidth = "1px"
        top.append(fst);
        //top.append(snd);

        bottom = document.createElement("div");
        bottom.className = "ptx-runestone-container";
        snd.className = "runestone assignment-nav bottom-assignment-nav";
        //bottom.style.backgroundColor = "var(--componentBgColor)"
        //bottom.style.borderColor = "var(--componentBorderColor)"
        //bottom.style.borderWidth = "1px"

        //bottom.append(active.cloneNode(true));
        //bottom.append(fst.cloneNode(true));
        bottom.append(snd);

        // check the body tag to see if it has a pretext class (no jquery)
        if (ptxbook) {
            //append parts to the header and progress container
            let content = document.getElementById("ptx-content");
            if (content) {
                content.insertBefore(top, content.firstChild);
            }
            let pc = document.getElementById("scprogresscontainer");
            if (pc) {
                pc.style.marginBottom = "20px";
                pc.appendChild(bottom);
            }
            return;
        }
        const mainContent = document.getElementById("main-content");
        if (mainContent && snd) {
            mainContent.insertBefore(top, mainContent.firstChild);
            mainContent.appendChild(bottom);
        }
    }
}


/**
 * Build the activity dictionary for the progress bar by scanning the page.
 *
 * This is the fallback used when the server did not send activity counts --
 * i.e. when nobody is logged in and the page is being browsed.  The keys are
 * the div_ids that ``updateProgress`` increments, so they have to be the ids of
 * the elements carrying ``data-component``.  Those are not always the first
 * child of the ``.runestone`` wrapper: PreTeXt, for instance, emits an
 * ``exercise-statement`` div ahead of the component for multiple choice
 * questions.  Keying off the first child in that case gave every such question
 * on the page the same empty key, so eight exercises counted as one.
 *
 * @param {ParentNode} root - where to look; defaults to the whole document.
 * @returns {Object} activity ids mapped to 0, plus the ``page`` pseudo-activity.
 */
// Navigation and reference pages rather than content: they carry no activities,
// so they get no progress bar, no completion button and no highlighting. Keep
// this as the single list -- it used to be spelled out separately in
// user-highlights.js and the sphinx progress template, and the copies had
// drifted apart (genindex.html was missing here, which left a progress bar on
// the Book Index page).
export const NON_CONTENT_PAGES = [
    "index.html",
    "genindex.html",
    "toctree.html",
    "toc.html",
    "navhelp.html",
    "assignments.html",
    "Exercises.html",
    "search.html",
];

export function isCourseStarted() {
    let startDate = eBookConfig.termStartDate; // format is "2026-09-22"
    if (!startDate) {
        return false;
    }
    let today = new Date();
    let start = new Date(startDate);
    return today >= start;
}

function courseNotStartedMessage() {
    const formattedDate = new Date(eBookConfig.termStartDate).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
    return ("Course has not started yet. Progress will be tracked starting on " + formattedDate);
}

/** True when pathname names one of the NON_CONTENT_PAGES. */
export function isNonContentPage(pathname = window.location.pathname) {
    let page = pathname.split("/").pop().toLowerCase();
    return NON_CONTENT_PAGES.some((p) => p.toLowerCase() === page);
}

export function countActivitiesInPage(root = document) {
    let activities = { page: 0 };
    root.querySelectorAll(".runestone").forEach(function (e, index) {
        // A component may be the wrapper itself or somewhere inside it. Nested
        // parts of a component (mchoice answers, feedback) also carry
        // data-component, but querySelector returns the outermost match first.
        let component = e.matches("[data-component]")
            ? e
            : e.querySelector("[data-component]");
        let id = component?.id || e.firstElementChild?.id;
        // Without an id we cannot match this activity to a completion event,
        // but it is still one activity: give it a key of its own so it is not
        // folded together with every other unidentified one on the page.
        activities[id || `unidentified-activity-${index}`] = 0;
    });
    return activities;
}

export class PageProgressBar {
    constructor(actDict) {
        this.possible = 0;
        // The page itself counts as one item and is attempted as soon as it is
        // opened. That is what lets a page with no activities on it still be
        // completed. 
        this.total = 1;
        if (actDict && "assignment_spec" in actDict) {
            this.assignment_spec = actDict.assignment_spec;
            delete actDict.assignment_spec;
        }
        if (actDict && Object.keys(actDict).length > 0) {
            this.activities = actDict;
        } else {
            this.activities = countActivitiesInPage();
        }
        // Both dicts are expected to carry a `page` entry for the page itself:
        // countActivitiesInPage() adds one, and the server sends the page's own
        // question row under that key. Older servers keyed that row by its
        // question name instead, which made the page look like an extra
        // activity; nothing we can do about that here, but keep the fallback so
        // a server that sends no page entry at all still lines up.
        if (!("page" in this.activities)) {
            this.activities.page = 1;
        }
        this.calculateProgress();
        // Hide the progress bar on navigation pages, which have no activities.
        if (isNonContentPage()) {
            const scprogresscontainer = document.getElementById(
                "scprogresscontainer",
            );
            if (scprogresscontainer) scprogresscontainer.style.display = "none";
        }
        this.renderProgress();
    }

    calculateProgress() {
        for (let k in this.activities) {
            if (k !== undefined) {
                this.possible++;
                if (this.activities[k] > 0) {
                    this.total++;
                }
            }
        }
    }

    get activitiesAttempted() {
        return Math.max(this.total, 0);
    }

    get activitiesPossible() {
        return Math.max(this.possible, 0);
    }

    renderProgress() {
        let value = 0;
        const scprogresstotal = document.getElementById("scprogresstotal");
        if (scprogresstotal)
            scprogresstotal.textContent = this.activitiesAttempted;
        const scprogressposs = document.getElementById("scprogressposs");
        if (scprogressposs)
            scprogressposs.textContent = this.activitiesPossible;
        try {
            value = (100 * this.total) / this.possible;
        } catch (e) {
            value = 0;
        }
        let progressText = document.getElementById("scprogress-activity-count");
        // Warn if course not started
        if (progressText && !isCourseStarted()) {
            progressText.appendChild(document.createTextNode(courseNotStartedMessage()));
        }
        // Replace #subchapterprogress div with a native <progress> element if not already done
        let subchapterprogress = document.getElementById("subchapterprogress");
        if (subchapterprogress && subchapterprogress.tagName !== "PROGRESS") {
            // Replace the div with a <progress> element
            const progressElem = document.createElement("progress");
            progressElem.id = "subchapterprogress";
            progressElem.max = 100;
            progressElem.value = value;
            // Copy over any classes from the old div
            progressElem.className = subchapterprogress.className;
            subchapterprogress.replaceWith(progressElem);
            subchapterprogress = progressElem;
        } else if (subchapterprogress) {
            subchapterprogress.max = 100;
            subchapterprogress.value = value;
        }
        if (this.assignment_spec) {
            // If the user has completed all activities, send the reading score.
            // This handles the case where there are no activities on the page or
            //  where the user completed activities on the assignment page and now
            //  is viewing the reading page.
            // activities_required counts activities the reader completes, so
            // compare against activitiesAttempted rather than total -- total
            // includes the page itself, which used to send the reading score
            // one activity early.
            let completeActivities = this.activitiesAttempted;
            let requiredActivities = this.assignment_spec.activities_required;
            if (
                requiredActivities === null ||
                requiredActivities === undefined
            ) {
                // Nothing specific is required on this page, so opening it is
                // enough. Record how many activities are here for the check in
                // updateProgress().
                requiredActivities = 0;
                this.assignment_spec.activities_required =
                    this.activitiesPossible;
            }
            if (completeActivities >= requiredActivities) {
                this.sendCompletedReadingScore().then(() => {
                    console.log("Reading score sent for page");
                    // wait a tick then mark the page complete
                    // this is needed to let the progress bar update before marking complete
                    setTimeout(() => {
                        let cb = document.getElementById("completionButton");
                        if (
                            cb &&
                            cb.textContent.toLowerCase() === "mark as completed"
                        ) {
                            cb.click();
                        }
                    }, 500);
                });
            }
        }
        if (!eBookConfig.isLoggedIn) {
            const subchapterDiv = document.getElementById("subchapterprogress");
            if (subchapterDiv) subchapterDiv.classList.add("loggedout");
        }
    }

    updateProgress(div_id) {
        this.activities[div_id]++;
        // Only update the progress bar on the first interaction with an object.
        if (this.activities[div_id] === 1) {
            this.total++;
            let val = (100 * this.total) / this.possible;
            const scprogresstotal2 = document.getElementById("scprogresstotal");
            if (scprogresstotal2)
                scprogresstotal2.textContent = this.activitiesAttempted;
            const scprogressposs2 = document.getElementById("scprogressposs");
            if (scprogressposs2)
                scprogressposs2.textContent = this.activitiesPossible;
            let subchapterprogress2 =
                document.getElementById("subchapterprogress");
            if (
                subchapterprogress2 &&
                subchapterprogress2.tagName === "PROGRESS"
            ) {
                subchapterprogress2.value = val;
            }
            if (
                this.assignment_spec &&
                this.assignment_spec.activities_required !== null &&
                this.activitiesAttempted >=
                this.assignment_spec.activities_required
            ) {
                console.log("Required activities completed");
                this.sendCompletedReadingScore().then(() => {
                    console.log("Reading score sent");
                });
            }
            if (
                val == 100.0 &&
                (function () {
                    const cb = document.getElementById("completionButton");
                    return (
                        cb &&
                        cb.textContent &&
                        cb.textContent.toLowerCase() === "mark as completed"
                    );
                })()
            ) {
                const cb = document.getElementById("completionButton");
                if (cb && typeof cb.click === "function") {
                    cb.click();
                }
            }
        }
    }

    async sendCompletedReadingScore() {
        let headers = new Headers({
            "Content-type": "application/json; charset=utf-8",
            Accept: "application/json",
        });
        let data = { ...this.assignment_spec };
        let request = new Request(
            `${eBookConfig.new_server_prefix}/logger/update_reading_score`,
            {
                method: "POST",
                body: JSON.stringify(data),
                headers: headers,
            },
        );
        try {
            let response = await fetch(request);
            if (!response.ok) {
                console.error(
                    `Failed to send reading score! ${response.statusText}`,
                );
            }
            data = await response.json();
        } catch (e) {
            console.error(`Error sending reading score ${e}`);
        }
    }
}

export var pageProgressTracker = {};

function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
}

let studyCluesConversationId = -1;

function appendStudyCluesMessage(messagesEl, role, text, isHtml = false) {
    const bubble = document.createElement("div");
    bubble.className = `studyclues-message ${role}`;
    if (isHtml) {
        bubble.innerHTML = text;
    } else {
        bubble.textContent = text;
    }
    messagesEl.appendChild(bubble);
    messagesEl.scrollTop = messagesEl.scrollHeight;
}

function createStudyCluesWidget() {
    if (document.getElementById("studyclues-fab")) {
        return;
    }

    const style = document.createElement("style");
    style.textContent = `
        #studyclues-fab {
            position: fixed;
            right: 24px;
            bottom: 24px;
            z-index: 9999;
            border: none;
            border-radius: 9999px;
            padding: 12px 18px;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
            color: #fff;
            background: #2563eb;
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
        }

        #studyclues-chat {
            position: fixed;
            right: 24px;
            bottom: 78px;
            z-index: 9999;
            width: min(420px, calc(100vw - 32px));
            height: min(520px, calc(100vh - 120px));
            background: #fff;
            border: 1px solid #d1d5db;
            border-radius: 12px;
            box-shadow: 0 14px 36px rgba(0, 0, 0, 0.25);
            display: none;
            flex-direction: column;
            overflow: hidden;
        }

        #studyclues-chat.open {
            display: flex;
        }

        .studyclues-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 10px 12px;
            border-bottom: 1px solid #e5e7eb;
            font-weight: 600;
            background: #f9fafb;
        }

        .studyclues-close {
            border: none;
            background: transparent;
            cursor: pointer;
            font-size: 16px;
            line-height: 1;
        }

        .studyclues-coach-toggle {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 12px;
            font-weight: 500;
            color: #4b5563;
        }

        .studyclues-coach-toggle input[type="checkbox"] {
            appearance: none;
            -webkit-appearance: none;
            width: 32px;
            height: 18px;
            border-radius: 9999px;
            background: #d1d5db;
            cursor: pointer;
            position: relative;
            transition: background 0.2s;
            flex-shrink: 0;
        }

        .studyclues-coach-toggle input[type="checkbox"]::after {
            content: "";
            position: absolute;
            top: 2px;
            left: 2px;
            width: 14px;
            height: 14px;
            border-radius: 50%;
            background: #fff;
            transition: transform 0.2s;
        }

        .studyclues-coach-toggle input[type="checkbox"]:checked {
            background: #2563eb;
        }

        .studyclues-coach-toggle input[type="checkbox"]:checked::after {
            transform: translateX(14px);
        }

        .studyclues-messages {
            flex: 1;
            overflow-y: auto;
            padding: 12px;
            display: flex;
            flex-direction: column;
            gap: 10px;
            background: var(--background);
            color: var(--bodyFont);
        }

        .studyclues-message {
            max-width: 85%;
            padding: 10px 12px;
            border-radius: 10px;
            white-space: pre-wrap;
            word-break: break-word;
            font-size: 14px;
            line-height: 1.4;
        }

        .studyclues-message.user {
            margin-left: auto;
            background: var(--componentBgColor);
            border: 1px solid #bfdbfe;
            color: var(--bodyFont);
        }

        .studyclues-message.assistant {
            margin-right: auto;
            background: var(--background);
            border: 1px solid var(--grayToWhite);
            color: var(--bodyFont);
        }

        .studyclues-message.studyclues-loading {
            color: var(--bodyFont);
            font-style: italic;
        }

        .studyclues-spinner {
            display: inline-flex;
            align-items: center;
            gap: 4px;
        }

        .studyclues-spinner-dot {
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background: #6b7280;
            opacity: 0.35;
            animation: studyclues-dot-pulse 1s infinite ease-in-out;
        }

        .studyclues-spinner-dot:nth-child(2) {
            animation-delay: 0.15s;
        }

        .studyclues-spinner-dot:nth-child(3) {
            animation-delay: 0.3s;
        }

        @keyframes studyclues-dot-pulse {
            0%,
            80%,
            100% {
                opacity: 0.35;
                transform: scale(1);
            }
            40% {
                opacity: 1;
                transform: scale(1.2);
            }
        }

        .studyclues-inputbar {
            display: flex;
            gap: 8px;
            padding: 10px;
            border-top: 1px solid #e5e7eb;
            background: #fff;
        }

        .studyclues-inputbar input {
            flex: 1;
            min-width: 0;
            border: 1px solid #d1d5db;
            border-radius: 8px;
            padding: 8px 10px;
            font-size: 14px;
        }

        .studyclues-inputbar button {
            border: none;
            border-radius: 8px;
            padding: 8px 12px;
            cursor: pointer;
            color: #fff;
            background: #2563eb;
            font-weight: 600;
        }
    `;
    document.head.appendChild(style);

    const button = document.createElement("button");
    button.id = "studyclues-fab";
    button.textContent = "StudyClues Chat";

    const chat = document.createElement("div");
    chat.id = "studyclues-chat";
    chat.innerHTML = `
        <div class="studyclues-header">
            <span>StudyClues</span>
            <label class="studyclues-coach-toggle">
                <input type="checkbox" id="studyclues-coach-mode" checked />
                Coach Mode
            </label>
            <button class="studyclues-close" aria-label="Close chat">✕</button>
        </div>
        <div class="studyclues-messages"></div>
        <div class="studyclues-inputbar">
            <input type="text" placeholder="Ask a question about this course..." />
            <button type="button">Send</button>
        </div>
    `;

    document.body.appendChild(button);
    document.body.appendChild(chat);

    const closeBtn = chat.querySelector(".studyclues-close");
    const messagesEl = chat.querySelector(".studyclues-messages");
    const inputEl = chat.querySelector(".studyclues-inputbar input");
    const sendBtn = chat.querySelector(".studyclues-inputbar button");
    const coachToggle = chat.querySelector("#studyclues-coach-mode");

    let coachMode = coachToggle.checked;
    coachToggle.addEventListener("change", () => {
        coachMode = coachToggle.checked;
    });

    const toggleChat = () => {
        chat.classList.toggle("open");
        if (chat.classList.contains("open")) {
            inputEl.focus();
        }
    };

    const sendMessage = async () => {
        let query = inputEl.value.trim();
        if (!query) {
            return;
        }
        var sectionInfo = "";
        // find the section title on the page to include in the initial query
        if (document.querySelector("body.pretext") && document.querySelector("section.section")) {
            let section = document.querySelector("section.section");
            let sectionTitle = section.querySelector("span.title").innerText;
            let sectionNumber =
                section.querySelector("span.codenumber").innerText;
            sectionInfo = `${sectionNumber} ${sectionTitle}`;
        } else {
            let sectionSpan = document.querySelector("span.section-number");
            if (sectionSpan) {
                sectionInfo = sectionSpan.parentElement.innerText.trim();
            }
        }
        if (studyCluesConversationId === -1) {
            if (sectionInfo) {
                query = `Regarding section "${sectionInfo}": ${query}`;
            }
        }
        rb.logBookEvent({
            event: "studyclues_query",
            act: `query: ${query}`,
            div_id: `${sectionInfo}`,
        });
        appendStudyCluesMessage(messagesEl, "user", query); // todo: make this conditional on being a book page and on the book being one of the supported books
        inputEl.value = "";
        sendBtn.disabled = true;

        const loadingBubble = document.createElement("div");
        loadingBubble.className =
            "studyclues-message assistant studyclues-loading";
        loadingBubble.innerHTML =
            'Thinking <span class="studyclues-spinner"><span class="studyclues-spinner-dot"></span><span class="studyclues-spinner-dot"></span><span class="studyclues-spinner-dot"></span></span>';
        messagesEl.appendChild(loadingBubble);
        messagesEl.scrollTop = messagesEl.scrollHeight;

        try {
            const response = await fetch(
                `/assignment/student/studyclues_query`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                    },
                    body: JSON.stringify({
                        query,
                        conversation_id: studyCluesConversationId,
                        coachMode,
                    }),
                },
            );

            if (!response.ok) {
                throw new Error(
                    `Request failed with status ${response.status}`,
                );
            }

            const payload = await response.json();
            const detail = payload?.detail || {};
            const studycluesResponse = detail?.response || {};
            const llmResponse = studycluesResponse?.llm_response;
            const references = studycluesResponse?.references || {};

            if (typeof detail.conversation_id === "number") {
                studyCluesConversationId = detail.conversation_id;
            }

            const markdownResponse = (
                llmResponse || "No response available from StudyClues."
            ).replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, text, key) => {
                const url = references[key]?.content_url;
                return url ? `[${text}](${url})` : match;
            });
            const formattedResponse = marked.parse(markdownResponse);
            // add a target="_blank" to all links in the formatted response
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = formattedResponse;
            tempDiv.querySelectorAll("a").forEach((a) => {
                a.setAttribute("target", "_blank");
            });
            const finalFormattedResponse = tempDiv.innerHTML;
            appendStudyCluesMessage(
                messagesEl,
                "assistant",
                finalFormattedResponse,
                true,
            );
        } catch (err) {
            appendStudyCluesMessage(
                messagesEl,
                "assistant",
                "Sorry, StudyClues is unavailable right now. Please try again.",
            );
            console.error("StudyClues chat error:", err);
        } finally {
            loadingBubble.remove();
            sendBtn.disabled = false;
            inputEl.focus();
        }
    };

    button.addEventListener("click", toggleChat);
    closeBtn.addEventListener("click", toggleChat);
    sendBtn.addEventListener("click", sendMessage);
    inputEl.addEventListener("keydown", (evt) => {
        if (evt.key === "Enter") {
            evt.preventDefault();
            sendMessage();
        }
    });
}

function shouldShowStudyCluesWidget() {
    if (
        !(
            location.pathname.includes("/ns/books/") ||
            location.pathname.includes("doAssignment")
        )
    ) {
        return false;
    }

    return eBookConfig.course_attrs?.studyClues === "True";
}

async function handlePageSetup() {
    var mess;
    if (eBookConfig.useRunestoneServices) {
        let headers = new Headers({
            "Content-type": "application/json; charset=utf-8",
            Accept: "application/json",
        });
        let data = {
            timezoneoffset: new Date().getTimezoneOffset() / 60,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };
        let RS_info = getCookie("RS_info");
        var tz_match = false;
        if (RS_info) {
            try {
                let cleaned = RS_info.replace(/\\054/g, ","); // handle octal comma encoding
                let info = JSON.parse(decodeURIComponent(cleaned));
                info = JSON.parse(decodeURIComponent(info));
                if (
                    info.timezone === data.timezone &&
                    info.tz_offset === data.timezoneoffset
                ) {
                    console.log(
                        "Timezone cookie matches, not sending timezone to server",
                    );
                    tz_match = true;
                }
            } catch (e) {
                console.error(
                    "Error parsing RS_info cookie, sending timezone to server",
                );
            }
        }
        if (tz_match === false) {
            // Set a cookie so we don't have to do this again for a while.
            let request = new Request(
                `${eBookConfig.new_server_prefix}/logger/set_tz_offset`,
                {
                    method: "POST",
                    body: JSON.stringify(data),
                    headers: headers,
                },
            );
            try {
                let response = await fetch(request);
                if (!response.ok) {
                    console.error(
                        `Failed to set timezone! ${response.statusText}`,
                    );
                }
                data = await response.json();
            } catch (e) {
                console.error(`Error setting timezone ${e}`);
            }
        }
    }

    // PTX generated pages may have stale HTML. Forcibly re-render the content
    // of the progress container. Any logic that modifies the progress container
    // should be done after this point, or it will be overwritten.
    const scprogresscontainer = document.getElementById(
        "scprogresscontainer",
    );
    if (scprogresscontainer)
        scprogresscontainer.innerHTML = `
            <div id="scprogress-activity-count">
                You have attempted <span id="scprogresstotal"></span> of
                <span id="scprogressposs"></span> activities on this page.
            </div>
            <div id="subchapterprogress" aria-label="Page progress"></div>
        `;

    console.log(`This page served by ${eBookConfig.served_by}`);
    if (eBookConfig.isLoggedIn) {
        mess = `username: ${eBookConfig.username}`;
        if (!eBookConfig.isInstructor) {
            const ipDropdown = document.getElementById("ip_dropdown_link");
            if (ipDropdown && typeof ipDropdown.remove === "function") {
                ipDropdown.remove();
            }
            const instPeer = document.getElementById("inst_peer_link");
            if (instPeer && typeof instPeer.remove === "function") {
                instPeer.remove();
            }
        }
        document.dispatchEvent(new Event("runestone:login"));

        addReadingList();

        // Warn if the course has not started yet
        if (!isCourseStarted()) {
            const ptxContent = document.getElementById("ptx-content");
            if (ptxContent) {
                const warningContainer = document.createElement("div");
                warningContainer.className = "ptx-runestone-container";
                const warningDiv = document.createElement("div");
                warningDiv.className = "course-not-started-warning alert alert-danger";
                warningDiv.textContent = courseNotStartedMessage();
                warningContainer.appendChild(warningDiv);
                ptxContent.insertBefore(warningContainer, ptxContent.firstChild);
            }
        }

        // Only show the StudyClues widget on book/assignment pages for courses with the studyClues attribute set.
        if (shouldShowStudyCluesWidget()) {
            createStudyCluesWidget();
        }
    } else {
        mess = "Not logged in";
        document.dispatchEvent(new Event("runestone:logout"));
        let bw = document.getElementById("browsing_warning");
        if (bw) {
            bw.innerHTML =
                "<p class='navbar_message'>Saving and Logging are Disabled</p>";
        }
        let aw = document.getElementById("ad_warning");
        if (aw) {
            aw.innerHTML =
                "<p class='navbar_message'>🚫 Log-in to Remove <a href='/admin/legal/ads'>Ads!</a> 🚫 &nbsp;</p>";
        }
    }
    document.querySelectorAll(".loggedinuser").forEach((el) => {
        el.innerHTML = mess;
    });

    pageProgressTracker = new PageProgressBar(eBookConfig.activities);
    notifyRunestoneComponents();
}

function setupNavbarLoggedIn() {
    const profileLink = document.getElementById("profilelink");
    if (profileLink) profileLink.style.removeProperty("display");
    const passwordLink = document.getElementById("passwordlink");
    if (passwordLink) passwordLink.style.removeProperty("display");
    const registerLink = document.getElementById("registerlink");
    if (registerLink) registerLink.style.display = "none";
    document.querySelectorAll("li.loginout").forEach((el) => {
        el.innerHTML = '<a href="/admin/auth/logout">Log Out</a>';
    });
}
document.addEventListener("runestone:login", setupNavbarLoggedIn);

function setupNavbarLoggedOut() {
    if (eBookConfig.useRunestoneServices) {
        console.log("setup navbar for logged out");
        const registerLink = document.getElementById("registerlink");
        if (registerLink) registerLink.style.removeProperty("display");
        const profileLink = document.getElementById("profilelink");
        if (profileLink) profileLink.style.display = "none";
        const passwordLink = document.getElementById("passwordlink");
        if (passwordLink) passwordLink.style.display = "none";
        const ipDropdown = document.getElementById("ip_dropdown_link");
        if (ipDropdown) ipDropdown.style.display = "none";
        const instPeer = document.getElementById("inst_peer_link");
        if (instPeer) instPeer.style.display = "none";
        document.querySelectorAll("li.loginout").forEach((el) => {
            el.innerHTML =
                '<a href="' +
                eBookConfig.app +
                '/default/user/login">Login</a>';
        });
        document.querySelectorAll(".footer").forEach((el) => {
            el.innerHTML = "user not logged in";
        });
    }
}
document.addEventListener("runestone:logout", setupNavbarLoggedOut);

function notifyRunestoneComponents() {
    // Runestone components wait until login process is over to load components because of storage issues. This triggers the `dynamic import machinery`, which then sends the login complete signal when this and all dynamic imports are finished.
    console.log("triggering runestone:pre-login-complete");
    document.dispatchEvent(new Event("runestone:pre-login-complete"));
}

function placeAdCopy() {
    if (typeof showAd !== "undefined" && showAd) {
        let adNum = Math.floor(Math.random() * 2) + 1;
        let adBlock = document.getElementById(`adcopy_${adNum}`);
        let rsElements = document.querySelectorAll(".runestone");
        if (rsElements.length > 0) {
            let randomIndex = Math.floor(Math.random() * rsElements.length);
            rsElements[randomIndex].after(adBlock);
            adBlock.style.display = "block";
        }
    }
}

// initialize stuff
document.addEventListener("DOMContentLoaded", function () {
    if (eBookConfig) {
        handlePageSetup();
        placeAdCopy();
    } else {
        if (typeof eBookConfig === "undefined") {
            console.log(
                "eBookConfig is not defined.  This page must not be set up for Runestone",
            );
        }
    }
});

// misc stuff
// todo:  This could be further distributed but making a video.js file just for one function seems dumb.
window.addEventListener("load", function () {
    // add the video play button overlay image
    document.querySelectorAll(".video-play-overlay").forEach(function (el) {
        el.style.backgroundImage =
            "url('{{pathto('_static/play_overlay_icon.png', 1)}}')";
    });

    // This function is needed to allow the dropdown search bar to work;
    // The default behaviour is that the dropdown menu closes when something in
    // it (like the search bar) is clicked
    document
        .querySelectorAll(".dropdown input, .dropdown label")
        .forEach(function (el) {
            el.addEventListener("click", function (e) {
                e.stopPropagation();
            });
        });

    // re-write some urls
    // This is tricker than it looks and you have to obey the rules for # anchors
    // The #anchors must come after the query string as the server basically ignores any part
    // of a url that comes after # - like a comment...
    if (location.href.includes("mode=browsing")) {
        let queryString = "?mode=browsing";
        document.querySelectorAll("a").forEach((link) => {
            let anchorText = "";
            if (
                link.href.includes("books/published") &&
                !link.href.includes("?mode=browsing")
            ) {
                if (link.href.includes("#")) {
                    let aPoint = link.href.indexOf("#");
                    anchorText = link.href.substring(aPoint);
                    link.href = link.href.substring(0, aPoint);
                }
                link.href = link.href.includes("?")
                    ? link.href + queryString.replace("?", "&") + anchorText
                    : link.href + queryString + anchorText;
            }
        });
    }
});

// The Bust Menu
/*
Course Home
Assignments
Progress
Peer Instruction (Student)
------
Instructor Dashboard
Peer Instruction (Instructor)
Author Interface (optional)
Editor Interface (optional)
Request Invoice
------
Change Course
Profile
Log Out
*/
window.addEventListener("DOMContentLoaded", function (event) {
    const oldDropDown = this.document.querySelector(
        ".dropdown-content[data-deprecated]",
    );
    if (oldDropDown) oldDropDown.remove();

    const itemTemplate = this.document.getElementById(
        "ptx-user-dropdown-content_item-template",
    );
    const sepTemplate = this.document.getElementById(
        "ptx-user-dropdown-content_separator-template",
    );
    const menuContentArea = this.document.getElementById(
        "ptx-user-dropdown_rs-content",
    );

    if (!itemTemplate || !sepTemplate || !menuContentArea) {
        console.warn(
            "Missing template or content area for user dropdown",
        );
        return;
    }

    function makeLink(url, title, icon = null, ariaLabel = null) {
        // All we should assume about the item template is that it has an anchor element
        const link = itemTemplate.content.cloneNode(true);
        const linkAnchor = link.querySelector("a");
        if (url) {
            linkAnchor.href = url;
        }
        linkAnchor.innerText = "";
        const contentSpan = document.createElement("span");
        contentSpan.style.display = "inline-flex";
        contentSpan.style.alignItems = "center";
        contentSpan.style.gap = "0.35rem";
        const iconElement = document.createElement("span");
        iconElement.setAttribute("aria-hidden", "true");
        iconElement.className = "material-symbols-outlined";
        iconElement.style.display = "inline-flex";
        iconElement.style.alignItems = "center";
        iconElement.style.justifyContent = "center";
        iconElement.style.width = "1.25em";
        iconElement.style.minWidth = "1.25em";
        if (icon) {
            iconElement.innerText = icon;
        } else {
            iconElement.style.visibility = "hidden";
            iconElement.innerText = "home";
        }
        contentSpan.appendChild(iconElement);
        let linkTitle = document.createElement("span");
        linkTitle.innerText = title;
        contentSpan.appendChild(linkTitle);
        linkAnchor.appendChild(contentSpan);
        if (ariaLabel) {
            linkAnchor.ariaLabel = ariaLabel;
        }
        if (!url) {
            const info = document.createElement("span");
            info.className = linkAnchor.className;
            if (title.length > 30) {
                info.innerText = title.slice(0, 12) + "..." + title.slice(-15);
                // add hover to show full title
                info.title = title;
                info.style.cursor = "help";
            } else {
                info.innerText = title;
            }
            info.style.display = "flex";
            info.style.justifyContent = "center";
            info.style.alignItems = "center";
            linkAnchor.replaceWith(info);
        }
        // return entire item template, not just link
        return link;
    }
    // limit length of eBookConfig.course to 30 characters to prevent overly long course names in the menu but preserve the last 15 characters
    menuContentArea.appendChild(makeLink(null, eBookConfig.course));
    menuContentArea.appendChild(sepTemplate.content.cloneNode(true));

    menuContentArea.appendChild(makeLink("/ns/course/index", "Course Home", "home"));
    menuContentArea.appendChild(
        makeLink("/assignment/student/chooseAssignment", "Assignments", "edit"),
    );
    menuContentArea.appendChild(
        makeLink("/assignment/peer/student", "Peer Instruction (Student)", "groups_3"),
    );
    menuContentArea.appendChild(
        makeLink("/assignment/student/studentreport", "Progress"),
    );
    if (eBookConfig.isInstructor) {
        menuContentArea.appendChild(sepTemplate.content.cloneNode(true));
        menuContentArea.appendChild(
            makeLink("/admin/instructor/menu", "Instructor Dashboard", "settings"),
        );
        menuContentArea.appendChild(
            makeLink(
                "/assignment/peer/instructor",
                "Peer Instruction (Instructor)",
                "groups_3"
            ),
        );
        // On runestone.academy the author server is its own host, so this has to
        // be absolute. Single-proxy deployments set authorServerUrl to /author.
        let authorUrl =
            (eBookConfig.authorServerUrl ||
                "https://author.runestone.academy/author") + "/";
        if (eBookConfig.isAuthor) {
            menuContentArea.appendChild(
                makeLink(authorUrl, "Author Dashboard"),
            );
        }
        if (eBookConfig.isEditor) {
            menuContentArea.appendChild(
                makeLink(authorUrl, "Editor Dashboard"),
            );
        }
        menuContentArea.appendChild(
            makeLink(
                "/assignment/instructor/invoice_request",
                "Request Invoice",
            ),
        );
    }
    menuContentArea.appendChild(sepTemplate.content.cloneNode(true));
    menuContentArea.appendChild(
        makeLink("/admin/auth/my_courses", "Change Course", "newsstand"),
    );
    menuContentArea.appendChild(makeLink("/admin/auth/profile", "Profile"));
    menuContentArea.appendChild(makeLink("/admin/auth/logout", "Log Out"));
    menuContentArea.appendChild(sepTemplate.content.cloneNode(true));
    const problemReportLink = makeLink(
        "/admin/problem/report",
        "Problem Report",
        "bug_report",
    );
    // Stash recent console output so the report form can pick it up and send it
    // along with the report.
    const problemReportAnchor = problemReportLink.querySelector("a");
    if (problemReportAnchor) {
        problemReportAnchor.addEventListener("click", () => {
            try {
                sessionStorage.setItem(RS_CONSOLE_LOG_KEY, rsGetConsoleLog());
            } catch (e) {
                // sessionStorage may be unavailable (e.g. private mode); the
                // form simply won't be prefilled with the log.
            }
        });
    }
    menuContentArea.appendChild(problemReportLink);
});

/**
 * Returns true if the string appears to contain LaTeX math.
 */
export function looksLikeLatexMath(text) {
    if (typeof text !== "string") return false;

    // Common LaTeX math delimiters
    const mathDelimiters = [
        /\$\$[\s\S]+?\$\$/, // $$ ... $$
        /\$[^$\n]+?\$/, // $ ... $
        /\\\[[\s\S]+?\\\]/, // \[ ... \]
        /\\\([\s\S]+?\\\)/, // \( ... \)
    ];

    // Common math commands (avoid generic \text or \ref-only cases)
    const mathCommands =
        /\\(frac|sqrt|sum|prod|int|lim|sin|cos|tan|log|ln|alpha|beta|gamma|pi|theta|sigma|Delta|cdot|times|leq|geq|neq)\b/;

    // Superscripts/subscripts like x^2 or a_i
    const superSubScript = /[a-zA-Z0-9]\s*[\^_]\s*\{?.+?\}?/;

    return (
        mathDelimiters.some((re) => re.test(text)) ||
        mathCommands.test(text) ||
        superSubScript.test(text)
    );
}
